const API_BASE = '/api';

interface ApiOptions {
  method?: string;
  body?: any;
  headers?: Record<string, string>;
}

async function apiRequest<T = any>(path: string, options: ApiOptions = {}): Promise<T> {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let fetchOptions: RequestInit = {
    method: options.method || 'GET',
    headers,
  };

  if (options.body instanceof FormData) {
    fetchOptions.body = options.body;
  } else if (options.body) {
    headers['Content-Type'] = 'application/json';
    fetchOptions.body = JSON.stringify(options.body);
    fetchOptions.headers = headers;
  }

  const res = await fetch(`${API_BASE}${path}`, fetchOptions);

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${res.status}`);
  }

  return res.json();
}

export const api = {
  get: <T = any>(path: string) => apiRequest<T>(path),
  post: <T = any>(path: string, body?: any) => apiRequest<T>(path, { method: 'POST', body }),
  put: <T = any>(path: string, body?: any) => apiRequest<T>(path, { method: 'PUT', body }),
  delete: <T = any>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
  upload: <T = any>(path: string, formData: FormData) =>
    apiRequest<T>(path, { method: 'POST', body: formData }),
};

export type PostType = 'event' | 'association' | 'community' | 'education';
export type PostStatus = 'draft' | 'pending' | 'published' | 'archived';
export type UserRole = 'admin' | 'editor' | 'staff';

export interface Post {
  id: number;
  title: string;
  subtitle?: string;
  slug: string;
  content?: string;
  post_type: PostType;
  status: PostStatus;
  featured: number;
  featured_image_url?: string;
  featured_design?: string;
  event_date?: string;
  event_time?: string;
  event_location?: string;
  event_end_date?: string;
  category?: string;
  button1_text?: string;
  button1_url?: string;
  button2_text?: string;
  button2_url?: string;
  ticket_url?: string;
  video_url?: string;
  author_id: number;
  approved_by?: number;
  published_at?: string;
  created_at: string;
  updated_at: string;
  media?: MediaItem[];
  video_links?: VideoLink[];
}

export interface MediaItem {
  id: number;
  post_id: number;
  filename: string;
  original_name: string;
  mime_type: string;
  size: number;
  r2_key: string;
  url: string;
  media_type: 'photo' | 'video';
  sort_order: number;
}

export interface VideoLink {
  id: number;
  post_id: number;
  url: string;
  platform: 'youtube' | 'onedrive' | 'dropbox' | 'r2' | 'other';
  title?: string;
  sort_order: number;
}

export interface User {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  active: number;
  created_at: string;
}

export interface SiteConfig {
  [key: string]: string;
}
